<template>
  <b-modal
    id="modal-filter"
    ok-title="Submit"
    cancel-variant="outline-secondary"
    centered
    title="過瀘"
    headerClass="pr-3 py-2 border-bottom bgWhite"
    footerClass="px-3 py-2 border-top bgWhite"
    hide-footer
    no-close-on-backdrop
    size="lg"
  >
    <b-overlay
      :show="loading"
      spinner-variant="primary"
      spinner-type="grow"
      spinner-small
      rounded="sm"
    >
      <b-form class="mt-1" @submit.prevent="onSubmit">
        <b-row>
          <!-- 收支者過濾 - 保持現有邏輯 -->
          <b-col cols="12">
            <b-form-group
              label="收支者："
              label-for="filter_user_id"
              label-cols-md="3"
            >
              <v-select
                id="filter_user_id"
                v-model="update.filter_user_id"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="userOptions.filter(item => item.user_type_id != 2 && item.user_type_id != 3)"
                label="name_cn"
                :reduce="(option) => option.id"
                placeholder="選擇收支者"
                :filter="userSearch"
              >
                <template #option="{ roles, name_cn }">
                  <template v-if="roles && roles[0]">
                    [{{ roles[0].name }}] {{ name_cn }}
                  </template>
                  <template v-else>
                    {{ name_cn }}
                  </template>
                </template>
                <template #selected-option="{ roles, name_cn }">
                  <template v-if="roles && roles[0]">
                    [{{ roles[0].name }}] {{ name_cn }}
                  </template>
                  <template v-else>
                    {{ name_cn }}
                  </template>
                </template>
              </v-select>
            </b-form-group>
          </b-col>

          <!-- 計薪形式篩選器 -->
          <b-col cols="12">
            <b-form-group
              label="計薪形式："
              label-for="filter_salary_type"
              label-cols-md="3"
            >
              <v-select
                id="filter_salary_type"
                v-model="update.filter_salary_type"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="salaryTypeOptions"
                label="name"
                :reduce="(option) => option.value"
                placeholder="選擇計薪形式"
              ></v-select>
            </b-form-group>
          </b-col>

          <!-- 發票過濾 - 只有在計薪形式為日薪(1)或以工程項目計算(3)時才顯示 -->
          <b-col cols="12" v-if="shouldShowInvoiceFilter">
            <b-form-group
              label="發票："
              label-for="filter_invoice_id"
              label-cols-md="3"
            >
              <v-select
                id="filter_invoice_id"
                v-model="update.filter_invoice_id"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="invoiceOptions"
                label="invoice_number"
                :reduce="(option) => option.id"
                placeholder="選擇發票"
              >
                <template #option="{ invoice_number, name_cn, address }">
                  [{{ invoice_number }}] {{ name_cn || '未指定客戶' }}{{ address ? ' ' + address : '' }}
                </template>
                <template #selected-option="{ invoice_number, name_cn, address }">
                  [{{ invoice_number }}] {{ name_cn || '未指定客戶' }}{{ address ? ' ' + address : '' }}
                </template>
              </v-select>
            </b-form-group>
          </b-col>

          <!-- 金額範圍過濾 -->
          <b-col cols="12">
            <b-form-group
              label="金額範圍："
              label-for="filter_amount_range"
              label-cols-md="3"
            >
              <div class="amount-range-container">
                <vue-slider
                  v-model="update.filter_amount_range"
                  :min="0"
                  :max="1000000"
                  :interval="1"
                  :tooltip="'always'"
                  :tooltip-formatter="formatTooltip"
                  :dot-size="20"
                  :height="6"
                  :process-style="{ backgroundColor: '#007bff' }"
                  :rail-style="{ backgroundColor: '#e9ecef' }"
                  :dot-style="{ backgroundColor: '#007bff', border: '2px solid #fff', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }"
                  class="mb-1"
                  @change="updateInputFields"
                />
                <div class="d-flex justify-content-between align-items-center mt-2">
                  <div class="d-flex align-items-center">
                    <span class="text-muted small mr-1">HK$</span>
                    <b-form-input
                      v-model="update.filter_min_amount_input"
                      type="number"
                      min="0"
                      max="1000000"
                      step="any"
                      placeholder="0"
                      size="sm"
                      style="width: 80px;"
                      @input="updateMinAmount"
                    />
                  </div>
                  <span class="font-weight-bold text-primary mx-2">
                    HK${{ formatAmount(update.filter_amount_range[0]) }} - HK${{ formatAmount(update.filter_amount_range[1]) }}
                  </span>
                  <div class="d-flex align-items-center">
                    <span class="text-muted small mr-1">HK$</span>
                    <b-form-input
                      v-model="update.filter_max_amount_input"
                      type="number"
                      min="0"
                      max="1000000"
                      step="any"
                      placeholder="1000000"
                      size="sm"
                      style="width: 100px;"
                      @input="updateMaxAmount"
                    />
                  </div>
                </div>
              </div>
            </b-form-group>
          </b-col>

          <!-- 薪金支出日期範圍 -->
          <b-col cols="12">
            <b-form-group
              label="出糧日期："
              label-for="filter_start_date"
              label-cols-md="3"
            >
              <div class="d-flex align-items-center gap-2">
                <b-form-datepicker
                  id="filter_start_date"
                  v-model="update.filter_start_date"
                  :max="update.filter_end_date"
                  placeholder="開始日期"
                  :date-format-options="{ year: 'numeric', month: '2-digit', day: '2-digit' }"
                  locale="zh-TW"
                  :state="dateValidationState.start"
                  reset-button
                  close-button
                  class="flex-1"
                />
                <span class="text-muted px-2">至</span>
                <b-form-datepicker
                  id="filter_end_date"
                  v-model="update.filter_end_date"
                  :min="update.filter_start_date"
                  placeholder="結束日期"
                  :date-format-options="{ year: 'numeric', month: '2-digit', day: '2-digit' }"
                  locale="zh-TW"
                  :state="dateValidationState.end"
                  reset-button
                  close-button
                  class="flex-1"
                />
              </div>
              <b-form-invalid-feedback v-if="dateValidationState.start === false || dateValidationState.end === false">
                開始日期不能晚於結束日期
              </b-form-invalid-feedback>
            </b-form-group>
          </b-col>

          <!-- 薪金支出狀態過濾 -->
          <b-col cols="12">
            <b-form-group
              label="薪金支出狀態："
              label-for="filter_expense_status_id"
              label-cols-md="3"
            >
              <v-select
                id="filter_expense_status_id"
                v-model="update.filter_expense_status_id"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="expenseStatusOptions"
                label="name"
                :reduce="(option) => option.id"
                placeholder="選擇薪金支出狀態"
              ></v-select>
            </b-form-group>
          </b-col>
        </b-row>
        <div class="d-flex mt-2 justify-content-end">
          <b-button
            v-ripple.400="'rgba(186, 191, 199, 0.15)'"
            variant="outline-secondary"
            @click="resetFilters"
            class="mr-1"
          >
            重置
          </b-button>
          <b-button
            v-ripple.400="'rgba(186, 191, 199, 0.15)'"
            type="button"
            variant="outline-danger"
            class="mr-1"
            @click="hide"
          >
            取消
          </b-button>
          <b-button
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            variant="primary"
            type="submit"
          >
            <feather-icon size="16" class="mr-1" icon="CheckCircleIcon" />
            提交
          </b-button>
        </div>
      </b-form>
    </b-overlay>
  </b-modal>
</template>

<script>
import {
  BButton,
  BModal,
  VBModal,
  BCardText,
  BOverlay,
  BForm,
  BFormGroup,
  BFormInput,
  BFormDatepicker,
  BFormInvalidFeedback,
  BRow,
  BCol,
  BCard,
  BCardBody,
} from "bootstrap-vue";
import vSelect from "vue-select";
import Ripple from "vue-ripple-directive";
import flatPickr from "vue-flatpickr-component";
import VueSlider from "vue-slider-component";
import "vue-slider-component/theme/antd.css";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";

export default {
  components: {
    BButton,
    BModal,
    VBModal,
    BCardText,
    BOverlay,
    BForm,
    BFormGroup,
    BFormInput,
    BFormDatepicker,
    BFormInvalidFeedback,
    BRow,
    BCol,
    BCardBody,
    BCard,
    vSelect,
    flatPickr,
    VueSlider,
  },
  directives: {
    "b-modal": VBModal,
    Ripple,
  },
  methods: {
    hide() {
      this.$bvModal.hide("modal-filter");
    },
    resetFilters() {
      this.update = {
        filter_invoice_id: null,
        filter_user_id: null,
        filter_salary_type: null,
        filter_expense_status_id: null,
        filter_start_date: null,
        filter_end_date: null,
        filter_amount_range: [0, 1000000],
        filter_min_amount_input: 0,
        filter_max_amount_input: 1000000,
      };
    },
    formatAmount(amount) {
      return new Intl.NumberFormat('zh-HK').format(amount);
    },
    formatTooltip(value) {
      return `HK$${this.formatAmount(value)}`;
    },
    updateInputFields(value) {
      this.update.filter_min_amount_input = value[0];
      this.update.filter_max_amount_input = value[1];
    },
    updateMinAmount(value) {
      const numValue = Number(value) || 0;
      if (numValue <= this.update.filter_amount_range[1]) {
        this.update.filter_amount_range = [numValue, this.update.filter_amount_range[1]];
      }
    },
    updateMaxAmount(value) {
      const numValue = Number(value) || 1000000;
      if (numValue >= this.update.filter_amount_range[0]) {
        this.update.filter_amount_range = [this.update.filter_amount_range[0], numValue];
      }
    },
    onSubmit() {
      // 驗證日期範圍
      if (this.dateValidationState.start === false || this.dateValidationState.end === false) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: "日期範圍錯誤",
            text: "開始日期不能晚於結束日期",
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
        return;
      }

      this.$emit("update:filter_invoice_id", this.update.filter_invoice_id);
      this.$emit("update:filter_user_id", this.update.filter_user_id);
      this.$emit("update:filter_salary_type", this.update.filter_salary_type);
      this.$emit("update:filter_expense_status_id", this.update.filter_expense_status_id);
      this.$emit("update:filter_start_date", this.update.filter_start_date);
      this.$emit("update:filter_end_date", this.update.filter_end_date);
      this.$emit("update:filter_min_amount", this.update.filter_min_amount_input);
      this.$emit("update:filter_max_amount", this.update.filter_max_amount_input);
      this.hide();
    },
    userSearch(options, search) {
      var returnOptions = options.filter((item) => {
        return (
          item.name_cn.toLowerCase().includes(search.toLowerCase())
        );
      });
      return returnOptions;
    },

  },
  props: {
    invoiceOptions: {
      type: Array,
      required: true,
    },
    userOptions: {
      type: Array,
      required: true,
    },
    expenseStatusOptions: {
      type: Array,
      required: true,
    },
    filter_invoice_id: {},
    filter_user_id: {},
    filter_salary_type: {},
    filter_expense_status_id: {},
    filter_start_date: {},
    filter_end_date: {},
    filter_min_amount: {},
    filter_max_amount: {},
  },
  data() {
    return {
      loading: false,
      update: {
        filter_invoice_id: null,
        filter_user_id: null,
        filter_salary_type: null,
        filter_expense_status_id: null,
        filter_start_date: null,
        filter_end_date: null,
        filter_amount_range: [0, 1000000],
        filter_min_amount_input: 0,
        filter_max_amount_input: 1000000,
      },
      // 計薪形式選項
      salaryTypeOptions: [
        { name: '全部', value: null },
        { name: '日薪', value: 1 },
        { name: '月薪', value: 2 },
        { name: '以工程項目計算', value: 3 }
      ],
    };
  },
  computed: {
    dateValidationState() {
      const start = this.update.filter_start_date;
      const end = this.update.filter_end_date;

      if (!start && !end) {
        return { start: null, end: null };
      }

      if (start && end) {
        const isValid = new Date(start) <= new Date(end);
        return {
          start: isValid ? true : false,
          end: isValid ? true : false,
        };
      }

      return { start: null, end: null };
    },
    // 計算是否應該顯示發票篩選器
    // 只有在計薪形式為日薪(1)或以工程項目計算(3)時才顯示
    shouldShowInvoiceFilter() {
      const salaryType = this.update.filter_salary_type;
      return salaryType === null || salaryType === 1 || salaryType === 3;
    },
  },
  watch: {
    // 監聽計薪形式變化，當選擇月薪時清空發票篩選器
    'update.filter_salary_type'(newValue) {
      // 如果選擇了月薪(2)，清空發票篩選器
      if (newValue === 2) {
        this.update.filter_invoice_id = null;
      }
    },
  },
};
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
@import "@core/scss/vue/libs/vue-flatpicker.scss";

.gap-2 {
  gap: 0.5rem;
}

.flex-1 {
  flex: 1;
}

// 金額範圍滑桿樣式
.amount-range-container {
  padding: 0.5rem 0;

  // Vue Slider 自定義樣式
  .vue-slider {
    margin: 1rem 0;
  }

  // 工具提示樣式
  .vue-slider-dot-tooltip {
    background-color: #007bff !important;
    border-color: #007bff !important;

    &::before {
      border-top-color: #007bff !important;
    }
  }

  // 金額輸入欄位樣式
  .form-control {
    border: 1px solid #d1d3e2;
    border-radius: 0.35rem;
    font-size: 0.875rem;
    text-align: center;

    &:focus {
      border-color: #007bff;
      box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
    }
  }
}

// 手機版響應式調整
@media (max-width: 576px) {
  .d-flex.align-items-center.gap-2 {
    flex-direction: column;
    align-items: stretch;

    .flex-1 {
      width: 100%;
    }

    .text-muted {
      text-align: center;
      padding: 0.25rem 0;
    }
  }
}
</style>
